import { useTranslation } from "react-i18next";
import { Skeleton, LabResultsList, LabResultDetails } from "@tellescope/ui";
import { Card } from "@tellescope/design-system";
import type { VitalDataBlock as VitalDataBlockType } from "../../../../shared/schema";
import { useVitalDataBlock } from "./useVitalDataBlock";

interface VitalDataBlockProps {
  block: VitalDataBlockType;
}

const VitalDataSkeleton = () => (
  <div className="space-y-2">
    {Array.from({ length: 4 }).map((_, i) => (
      <Card variant="floating" padding="root" key={i}>
        <Card.Body className="px-4">
          <div className="flex gap-4">
            <Skeleton className="w-14 h-14 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        </Card.Body>
      </Card>
    ))}
  </div>
);

export const VitalDataBlock = ({ block }: VitalDataBlockProps) => {
  const { t } = useTranslation("portal");
  const {
    labResults,
    selectedResult,
    selectResult,
    clearSelection,
    isLoading,
    error,
    screeningDates,
    selectedDate,
    setSelectedDate,
  } = useVitalDataBlock();

  if (isLoading) {
    return <VitalDataSkeleton />;
  }

  if (error) {
    return (
      <Card variant="floating" padding="root" className="border-destructive">
        <Card.Body className="p-4 text-center text-destructive">{t("Failed to load vital data")}</Card.Body>
      </Card>
    );
  }

  if (labResults.length === 0) {
    return (
      <Card variant="floating" padding="root">
        <Card.Body className="p-6 text-center text-muted-foreground">
          {block.props.emptyMessage ?? t("No vital data available")}
        </Card.Body>
      </Card>
    );
  }

  if (selectedResult) {
    return (
      <div className="rounded-lg border bg-card overflow-hidden" style={{ height: "600px" }}>
        <LabResultDetails result={selectedResult} onBack={clearSelection} />
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card overflow-hidden" style={{ height: "600px" }}>
      <LabResultsList
        results={labResults}
        onSelect={selectResult}
        screeningDates={screeningDates}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
      />
    </div>
  );
};
